import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    createTelegramChannelAssignment,
    getTelegramChannelAssignmentById,
    updateTelegramChannelAssignment,
    getTelegramEntityTypes,
    getCarriers,
    logError
} from '../api';
import { Button, Form, Container } from 'react-bootstrap';
import EditCancelModal from './Global/EditCancelModal';

const TelegramChannelAssignmentForm = () => {
    const [assignment, setAssignment] = useState({
        entityTypeValueId: '',
        entityReferenceId: '',
        telegramName: ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [entityTypes, setEntityTypes] = useState([]);
    const [referenceEntities, setReferenceEntities] = useState([]);
    const [selectedEntityType, setSelectedEntityType] = useState(null);
    const [error, setError] = useState('');

    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        getTelegramEntityTypes()
            .then(types => {
                setEntityTypes(types);
                if (id) {
                    setIsEditing(true);
                    getTelegramChannelAssignmentById(id)
                        .then(data => {
                            const entityType = types.find(et => et.valueId === data.entityTypeValueId);
                            const { telegramChannelId, ...assignmentData } = data;
                            setAssignment({
                                ...assignmentData,
                                entityTypeValueId: entityType?.id || data.entityTypeValueId
                            });
                            setSelectedEntityType(entityType);
                            if (data.entityTypeValueId) {
                                loadReferenceEntities();
                            }
                        })
                        .catch(logError);
                }
            })
            .catch(logError);
    }, [id]);

    const loadReferenceEntities = async () => {
        try {
            const entities = await getCarriers(true);
            setReferenceEntities(entities);
        } catch (error) {
            logError(error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setAssignment(prev => ({ ...prev, [name]: value }));
        setIsDirty(true);
        setError(''); // Clear error when user makes changes
        
        if (name === 'entityTypeValueId') {
            setSelectedEntityType(entityTypes.find(et => et.id === parseInt(value)));
            setAssignment(prev => ({ ...prev, entityReferenceId: '' }));
            loadReferenceEntities();
        }
    };

    const getEntityDisplayName = (entity) => {
        return `${entity.firstName} ${entity.lastName1}`;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const selectedType = entityTypes.find(et => et.id === parseInt(assignment.entityTypeValueId));
        const { telegramChannelId, ...assignmentData } = assignment;
        const assignmentToSubmit = {
            ...assignmentData,
            entityTypeValueId: selectedType?.valueId || parseInt(assignment.entityTypeValueId),
            entityReferenceId: parseInt(assignment.entityReferenceId) || 0
        };

        if (isEditing) {
            updateTelegramChannelAssignment(id, assignmentToSubmit)
                .then(() => navigate('/telegram-channel-assignments'))
                .catch(err => {
                    const errorMessage = err.response?.data?.message || err.message || 'Error al actualizar la asignación';
                    setError(errorMessage);
                    logError(err);
                });
        } else {
            createTelegramChannelAssignment(assignmentToSubmit)
                .then(() => navigate('/telegram-channel-assignments'))
                .catch(err => {
                    const errorMessage = err.response?.data?.message || err.message || 'Error al crear la asignación';
                    setError(errorMessage);
                    logError(err);
                });
        }
    };

    const handleCancel = () => {
        if (isDirty) setShowCancelModal(true);
        else navigate('/telegram-channel-assignments');
    };

    return (
        <Container className="my-4">
            <h2>{isEditing ? 'Editar Asignación' : 'Nueva Asignación'}</h2>

            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                    <Form.Label>Tipo de Entidad</Form.Label>
                    <Form.Control 
                        as="select" 
                        name="entityTypeValueId" 
                        value={assignment.entityTypeValueId} 
                        onChange={handleChange} 
                        required 
                    >
                        <option value="">Seleccionar tipo...</option>
                        {entityTypes.map(type => (
                            <option key={type.id} value={type.id}>{type.displayName || type.name}</option>
                        ))}
                    </Form.Control>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Transportista</Form.Label>
                    <Form.Control 
                        as="select" 
                        name="entityReferenceId" 
                        value={assignment.entityReferenceId} 
                        onChange={handleChange} 
                        disabled={!assignment.entityTypeValueId}
                    >
                        <option value="0">Sin asignar</option>
                        {referenceEntities.map(entity => (
                            <option key={entity.id} value={entity.id}>{getEntityDisplayName(entity)}</option>
                        ))}
                    </Form.Control>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Nombre de Telegram</Form.Label>
                    <Form.Control 
                        type="text" 
                        name="telegramName" 
                        value={assignment.telegramName} 
                        onChange={handleChange} 
                        maxLength={100}
                        required 
                    />
                </Form.Group>

                <div className="d-flex justify-content-between mt-4">
                    <Button variant="primary" type="submit">
                        {isEditing ? 'Guardar Cambios' : 'Crear Asignación'}
                    </Button>
                    <Button variant="secondary" onClick={handleCancel}>
                        Cancelar
                    </Button>
                </div>
            </Form>

            <EditCancelModal
                show={showCancelModal}
                onConfirm={() => navigate('/telegram-channel-assignments')}
                onCancel={() => setShowCancelModal(false)}
            />
        </Container>
    );
};

export default TelegramChannelAssignmentForm;